import { countries, type Country } from "./countries";

/** Countries that need strict national length enforcement. Extend as needed. */
const NATIONAL_LENGTHS: Record<string, number> = {
  US: 10,
  TJ: 9,
};

export const getNationalLengthForCountry = (code?: string): number => {
  if (!code) return 15;
  return NATIONAL_LENGTHS[code] ?? 15;
};

export const getDialCodeByCountry = (code: string): string | undefined =>
  countries.find((c) => c.code === code)?.phone;

export const requiresStrictNationalLength = (code?: string): boolean =>
  !!(code && Object.prototype.hasOwnProperty.call(NATIONAL_LENGTHS, code));

export const sanitizePhoneDigits = (value: string): string =>
  (value || "").replace(/\D+/g, "");

/** Build an E.164 formatted number: +{dial}{nationalDigits} */
export const toE164 = (
  countryCode: string | undefined,
  national: string,
): string => {
  const dial = countryCode ? getDialCodeByCountry(countryCode) : undefined;
  const nationalDigits = sanitizePhoneDigits(national);
  if (!dial || !nationalDigits) return nationalDigits;
  return `+${dial}${nationalDigits}`;
};

export const inferCountryCodeFromPhone = (
  phone: string,
): string | undefined => {
  const digits = sanitizePhoneDigits(phone);
  if (!digits) return undefined;

  let matchedCode: string | undefined;
  let matchedLength = 0;

  countries.forEach((country: Country) => {
    const dialDigits = country.phone.replace(/\D+/g, "");
    if (
      dialDigits &&
      digits.startsWith(dialDigits) &&
      dialDigits.length > matchedLength
    ) {
      matchedCode = country.code;
      matchedLength = dialDigits.length;
    }
  });

  return matchedCode;
};

export const stripDialCodeFromPhone = (
  phone: string,
  countryCode: string | undefined,
): string => {
  const digits = sanitizePhoneDigits(phone);
  if (!countryCode) return digits;
  const dial = getDialCodeByCountry(countryCode);
  if (!dial || !digits.startsWith(dial)) return digits;
  return digits.slice(dial.length);
};

export const trimPhoneToNationalLength = (
  countryCode: string | undefined,
  value: string,
): string =>
  sanitizePhoneDigits(value).slice(0, getNationalLengthForCountry(countryCode));
